import * as React from "react"
import { graphql } from "gatsby"
import "slick-carousel/slick/slick.css"
import "slick-carousel/slick/slick-theme.css"
import AdvisoryContent from "./single-product-advisory"
import GalleryContent from "./single-product-gallery"
import { useLocation } from "@reach/router"
import { Seo } from "../../components/seo"

export default function Product({ data, pageContext }) {
  const location = useLocation()
  const isAdvisoryPath = location.pathname.includes(
    "/advisory/nfollow/nindex/products/"
  )

  return (
    <>
      {isAdvisoryPath ? (
        <AdvisoryContent data={data} pageContext={pageContext} />
      ) : (
        <GalleryContent data={data} pageContext={pageContext} />
      )}
    </>
  )
}

export const Head = ({ data, pageContext }) => {
  const product = data.storefront.products.nodes.find(
    (node) => node.handle === pageContext.handle
  )

  return <Seo title={product.title} />
}

export const query = graphql`
  query SingleProduct {
    storefront {
      products(first: 250) {
        nodes {
          handle
          descriptionHtml
          createdAt
          availableForSale
          id
          isGiftCard
          onlineStoreUrl
          publishedAt
          requiresSellingPlan
          tags
          title
          totalInventory
          vendor
          images(first: 250) {
            nodes {
              height
              url
              altText
            }
          }
          media(first: 250) {
            nodes {
              ... on Storefront_Model3d {
                id
                sources {
                  format
                  url
                }
                previewImage {
                  url
                }
              }
            }
          }
          variants(first: 1) {
            nodes {
              sku
              id
            }
          }
          priceRange {
            maxVariantPrice {
              amount
              currencyCode
            }
          }
          subheading: metafield(
            key: "product_subheading"
            namespace: "custom"
          ) {
            value
            updatedAt
            reference {
              ... on Storefront_Metaobject {
                id
                fields {
                  value
                }
              }
            }
          }
          advisoryInformation: metafields(
            identifiers: { key: "advisory_information", namespace: "custom" }
          ) {
            id
            reference {
              ... on Storefront_Metaobject {
                id
                framingData: field(key: "framing_data") {
                  value
                }
                valuationsData: field(key: "valuations_data") {
                  value
                }
                managementFeeData: field(key: "management_fees_data") {
                  value
                }
                valueOfWorkData: field(key: "value_of_work_data") {
                  value
                }
                totalCostofPortfolioIncludingSundriesData: field(
                  key: "total_cost_of_portfolio_including_sundries_data"
                ) {
                  value
                }
                rentalTermsData: field(key: "rental_terms") {
                  value
                }
                rentalreturnData: field(key: "rental_returns") {
                  value
                }
              }
            }
          }
          medium: metafield(key: "artwork_medium", namespace: "custom") {
            references(first: 250) {
              nodes {
                ... on Storefront_Metaobject {
                  id
                  fields {
                    value
                  }
                }
              }
            }
          }
          display_add_to_cart: metafield(
            key: "display_add_to_cart"
            namespace: "custom"
          ) {
            value
          }
          artworksubstrate: metafield(
            key: "artwork_substrate"
            namespace: "custom"
          ) {
            reference {
              ... on Storefront_Metaobject {
                id
                fields {
                  value
                }
              }
            }
          }
          dimension: metafield(key: "product_dimensions", namespace: "custom") {
            reference {
              ... on Storefront_Metaobject {
                id
                fields {
                  value
                }
              }
            }
          }
          Locationview: metafields(
            identifiers: {
              key: "where_is_artwork_available_to_view_"
              namespace: "custom"
            }
          ) {
            createdAt
            reference {
              ... on Storefront_Metaobject {
                id
                fields {
                  value
                }
              }
            }
          }
          metafield(key: "select_template", namespace: "custom") {
            value
            updatedAt
            reference {
              ... on Storefront_Metaobject {
                id
                fields {
                  value
                }
              }
            }
          }
          metafields(
            identifiers: [{ namespace: "custom", key: "what_artworks_says" }]
          ) {
            createdAt
            reference {
              ... on Storefront_Metaobject {
                id
                fields {
                  reference {
                    ... on Storefront_Video {
                      id
                      sources {
                        url
                      }
                      alt
                    }
                    ... on Storefront_MediaImage {
                      id
                      image {
                        url
                        altText
                      }
                    }
                  }
                  value
                }
              }
            }
          }
        }
      }
      collections(first: 250) {
        nodes {
          descriptionHtml
          id
          title
          handle
          image {
            url
            altText
          }
          auction_results: metafields(
            identifiers: { key: "auction_results", namespace: "custom" }
          ) {
            id
            references(first: 250) {
              nodes {
                ... on Storefront_Metaobject {
                  auction_date: field(key: "auction_date") {
                    value
                  }
                  artwork_title_and_year: field(key: "artwork_title_and_year") {
                    value
                  }
                  artwork_auction_estimated_price: field(
                    key: "artwork_auction_estimated_price"
                  ) {
                    value
                  }
                  percentage_above_estimate: field(
                    key: "percentage_above_estimate"
                  ) {
                    value
                  }
                  artwork_auction_hammer_price: field(
                    key: "artwork_auction_hammer_price"
                  ) {
                    value
                  }
                  auction_house_name: field(key: "auction_house_name") {
                    value
                  }
                  auction_house_location_of_auctioneer: field(
                    key: "auction_house_location_of_auctioneer"
                  ) {
                    value
                  }
                  artwork_size: field(key: "artwork_size") {
                    value
                  }
                  auction_artwork_image: field(key: "auction_artwork_image") {
                    reference {
                      ... on Storefront_MediaImage {
                        image {
                          url
                        }
                      }
                    }
                  }
                  id
                }
              }
            }
          }
          metafields(
            identifiers: [
              { namespace: "custom", key: "explore_artist_by" }
              { namespace: "custom", key: "artist_format" }
              { namespace: "custom", key: "artist_career_stages" }
              { namespace: "custom", key: "artist_region" }
              { namespace: "custom", key: "collection_related_to_artist" }
              { namespace: "custom", key: "similar_artists" }
              { namespace: "custom", key: "is_this_an_artist_collection" }
              { namespace: "custom", key: "auction_results" }
              { namespace: "custom", key: "pdf" }
            ]
          ) {
            namespace
            key
            value
            references(first: 250) {
              nodes {
                ... on Storefront_Metaobject {
                  handle
                  fields {
                    value
                    reference {
                      ... on Storefront_MediaImage {
                        id
                        image {
                          url
                        }
                      }
                    }
                  }
                }
              }
            }
            reference {
              ... on Storefront_Metaobject {
                handle
                field(key: "artists_photo") {
                  reference {
                    ... on Storefront_MediaImage {
                      image {
                        url
                        altText
                      }
                    }
                  }
                }
                pdfFile: field(key: "pdf") {
                  key
                  reference {
                    ... on Storefront_GenericFile {
                      url
                    }
                  }
                }
              }
            }
          }
        }
      }
      metaobjects(first: 250, type: "artists_list") {
        edges {
          node {
            handle
            fields {
              value
              type
              key
            }
            field(key: "artist_featured_photo") {
              value
              key
              reference {
                ... on Storefront_MediaImage {
                  id
                  image {
                    url
                    id
                    altText
                    height
                    width
                  }
                }
              }
            }
            artistname: field(key: "artist_name") {
              value
              key
              reference {
                ... on Storefront_Metaobject {
                  id
                  handle
                  fields {
                    value
                  }
                }
              }
            }
            artistdescription: field(key: "artists_description") {
              value
              key
              reference {
                ... on Storefront_Metaobject {
                  id
                  handle
                  fields {
                    value
                  }
                }
              }
            }
            bioimages: field(key: "artistimages") {
              value
              key
              references(first: 10) {
                nodes {
                  ... on Storefront_MediaImage {
                    id
                    image {
                      altText
                      url
                    }
                  }
                }
              }
            }
            exhibition: field(key: "exhibition") {
              value
              key
              references(first: 250) {
                nodes {
                  ... on Storefront_Metaobject {
                    id
                    type
                    handle
                    fields {
                      value
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`
